import { Component } from '@theme/component';
import { preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Let the browser coalesce clicks from the same event turn without delaying a
// normal add-to-cart click. A visible debounce here made every add feel slow.
const CART_ADD_BATCH_IDLE_WINDOW = 0;

// `cart-drawer` is a static Shopify section, so its filename is also the
// Section Rendering API ID. Request it with the mutation to avoid a second
// network round trip before the drawer can show the updated cart.
const CART_DRAWER_SECTION_ID = 'cart-drawer';

// A failed/stalled variant section request must not leave optimistic cart
// operations pending forever.
const VARIANT_ADD_QUEUE_TIMEOUT = 8000;

// Creating many fly-to-cart elements at once adds layout and animation work on
// the main thread. Keep the feedback, but cap the expensive animation on spam.
const FLY_TO_CART_THROTTLE = 250;

let lastFlyToCartAnimation = 0;
let cartOperationSequence = 0;
/** @type {Promise<any> | null} */
let cartDataRequest = null;
/** @type {Promise<any> | null} */
let cartReconciliationRequest = null;

/**
 * Deduplicates simultaneous cart reads from the many product forms that can be
 * present on a collection page.
 */
function fetchCartData(forceFresh = false) {
  if (!forceFresh && cartDataRequest) return cartDataRequest;

  const request = fetch(`${Theme.routes.cart_url}.js`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    })
    .finally(() => {
      if (cartDataRequest === request) cartDataRequest = null;
    });

  if (!forceFresh) cartDataRequest = request;
  return request;
}

/**
 * Reads the authoritative cart after an ambiguous/failed mutation. Shopify can
 * partially apply some validation failures, so retrying or blindly rolling
 * back the optimistic delta is unsafe.
 */
function reconcileCartState() {
  if (cartReconciliationRequest) return cartReconciliationRequest;

  const request = fetch(`${Theme.routes.cart_url}.js`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to reconcile cart: ${response.status}`);
      return response.json();
    })
    .then((cart) => {
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-api-reconciliation', {
          source: 'cart-api-reconciliation',
          itemCount: Number(cart.item_count) || 0,
        })
      );
      return cart;
    })
    .catch((error) => {
      console.error('Failed to reconcile cart state:', error);
      return null;
    })
    .finally(() => {
      if (cartReconciliationRequest === request) cartReconciliationRequest = null;
    });

  cartReconciliationRequest = request;
  return request;
}

/** @returns {string} */
function createCartOperationId() {
  cartOperationSequence += 1;
  return `cart-add-${Date.now()}-${cartOperationSequence}`;
}

/**
 * Converts the documented product-form fields to a Cart Ajax API item.
 * @param {FormData} formData
 */
function cartItemFromFormData(formData) {
  const rawId = formData.get('id');
  if (typeof rawId !== 'string' || !rawId) throw new Error('Form ID is required');

  /** @type {{id: number|string, quantity: number, selling_plan?: string, properties?: Record<string, string>}} */
  const item = {
    id: Number.isSafeInteger(Number(rawId)) ? Number(rawId) : rawId,
    quantity: Number(formData.get('quantity')) || 1,
  };

  const sellingPlan = formData.get('selling_plan');
  if (typeof sellingPlan === 'string' && sellingPlan) item.selling_plan = sellingPlan;

  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[(.+)]$/.exec(key);
    if (!match || typeof value !== 'string') continue;
    properties[match[1]] = value;
  }

  if (Object.keys(properties).length > 0) item.properties = properties;
  return item;
}

/**
 * Combines identical items so a rapid click burst produces one compact request.
 * @param {Array<ReturnType<typeof cartItemFromFormData>>} items
 */
function mergeCartItems(items) {
  /** @type {Map<string, ReturnType<typeof cartItemFromFormData>>} */
  const merged = new Map();

  for (const item of items) {
    const properties = Object.entries(item.properties ?? {}).sort(([a], [b]) => a.localeCompare(b));
    const key = JSON.stringify([item.id, item.selling_plan ?? null, properties]);
    const existing = merged.get(key);

    if (existing) {
      existing.quantity += item.quantity;
    } else {
      merged.set(key, { ...item, properties: item.properties ? { ...item.properties } : undefined });
    }
  }

  return Array.from(merged.values());
}

class CartAddBatcher {
  /** @type {Array<{item: ReturnType<typeof cartItemFromFormData>, resolve: (value: any) => void, reject: (reason?: any) => void}>} */
  #queue = [];

  /** @type {number | undefined} */
  #timer;

  /** @type {boolean} */
  #inFlight = false;

  /** @param {ReturnType<typeof cartItemFromFormData>} item */
  add(item) {
    return new Promise((resolve, reject) => {
      this.#queue.push({ item, resolve, reject });
      this.#scheduleFlush();
    });
  }

  // A product link can be clicked before the normal debounce expires. Start
  // the queued mutation before the old page is discarded so the destination
  // page is not rendered against an avoidably stale cart.
  flushBeforePageExit() {
    if (this.#timer !== undefined) {
      clearTimeout(this.#timer);
      this.#timer = undefined;
    }

    if (!this.#inFlight && this.#queue.length > 0) {
      void this.#flush();
    }
  }

  #scheduleFlush() {
    if (this.#inFlight || this.#queue.length === 0) return;

    if (this.#timer !== undefined) clearTimeout(this.#timer);

    this.#timer = setTimeout(this.#flush, CART_ADD_BATCH_IDLE_WINDOW);
  }

  #flush = async () => {
    this.#timer = undefined;
    if (this.#inFlight) return;

    const batch = this.#queue.splice(0);
    if (batch.length === 0) return;
    this.#inFlight = true;

    try {
      const body = {
        items: mergeCartItems(batch.map(({ item }) => item)),
        sections: [CART_DRAWER_SECTION_ID],
        sections_url: `${window.location.pathname}${window.location.search}`,
      };

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        // The request body stays small while Shopify returns the already
        // rendered drawer in the same response as the cart mutation.
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      const result = await response.json();

      // Only the last completed batch may update the cart UI. Earlier responses
      // describe a partial cart when the customer clicks again mid-request.
      result.cartBatchFinal = this.#queue.length === 0;

      // Shopify normally includes status in error JSON. Preserve the same
      // contract if an intermediary returns JSON without it.
      if (!response.ok && !result.status) result.status = response.status;
      for (const request of batch) request.resolve(result);
    } catch (error) {
      for (const request of batch) request.reject(error);
    } finally {
      this.#inFlight = false;

      if (this.#queue.length > 0) {
        this.#scheduleFlush();
      }
    }
  };
}

const cartAddBatcher = new CartAddBatcher();

window.addEventListener('pagehide', () => cartAddBatcher.flushBeforePageExit());

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const now = performance.now();
    if (now - lastFlyToCartAnimation < FLY_TO_CART_THROTTLE) return;
    lastFlyToCartAnimation = now;

    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number, operationId: string}>} */
  #addToCartQueue = [];

  /** @type {number} */
  #cartQuantityRequestId = 0;

  /** @type {number | undefined} */
  #variantQueueTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdateError, this.#onVariantUpdateError, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    if (this.#variantQueueTimeout !== undefined) {
      clearTimeout(this.#variantQueueTimeout);
      this.#variantQueueTimeout = undefined;
    }

    if (this.#addToCartQueue.length > 0) {
      this.#failQueuedAdds('Product form was closed before the selected variant could be confirmed.');
    }

    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    const requestId = ++this.#cartQuantityRequestId;

    try {
      const cart = await fetchCartData();

      // A slower, older response must not overwrite a newer cart quantity.
      if (requestId === this.#cartQuantityRequestId) {
        this.#updateCartQuantityFromData(cart);
      }
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === 'product-form-component' ||
      event.detail?.data?.source === 'cart-section-reconciliation' ||
      event.detail?.data?.source === 'cart-api-reconciliation'
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        const operationId = createCartOperationId();
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity, operationId });
        this.#scheduleVariantQueueTimeout();
        this.dispatchEvent(
          new CartAddEvent(undefined, intendedVariantId, {
            source: 'product-form-component',
            phase: 'pending',
            optimistic: true,
            operationIds: [operationId],
            itemCount: quantity,
            productId: this.dataset.productId,
            variantId: intendedVariantId,
            productTitle: this.dataset.productTitle,
            productImage: this.refs.addToCartButtonContainer?.dataset.productVariantMedia,
            canOptimisticallyUpdateLine: true,
          })
        );
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    // Each click gets immediate optimistic feedback. Network mutations are
    // micro-batched below instead of waiting in a slow per-click queue.
    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('form input[name="quantity"]'));

    return (
      Number(this.refs.quantitySelector?.getValue?.()) ||
      Number(quantityInput?.value) ||
      Number(this.dataset.quantityDefault) ||
      1
    );
  }

  #scheduleVariantQueueTimeout() {
    if (this.#variantQueueTimeout !== undefined) return;

    this.#variantQueueTimeout = setTimeout(() => {
      this.#variantQueueTimeout = undefined;
      this.#variantChangeInProgress = false;
      this.#failQueuedAdds('Timed out while confirming the selected product variant.');
    }, VARIANT_ADD_QUEUE_TIMEOUT);
  }

  /** @param {string} message */
  #failQueuedAdds(message) {
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = this.#addToCartQueue.splice(0);
    const operationIds = queuedItems.map((item) => item.operationId);
    const itemCount = queuedItems.reduce((sum, item) => sum + item.quantity, 0);

    document.dispatchEvent(
      new CartAddEvent({}, this.id, {
        source: 'product-form-component',
        optimistic: true,
        operationIds,
        itemCount,
        productId: this.dataset.productId,
        didError: true,
        phase: 'failed',
      })
    );
    document.dispatchEvent(new CartErrorEvent(this.id, message, message, {}));
    void reconcileCartState();
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const id = formData.get('id');
    if (!id) throw new Error('Form ID is required');

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const cartItem = cartItemFromFormData(formData);
    const operationId = createCartOperationId();
    const eventData = {
      source: 'product-form-component',
      optimistic: true,
      operationIds: [operationId],
      itemCount,
      productId: this.dataset.productId,
      variantId: id.toString(),
      productTitle: this.dataset.productTitle,
      productImage: this.refs.addToCartButtonContainer?.dataset.productVariantMedia,
      canOptimisticallyUpdateLine: !cartItem.selling_plan && !cartItem.properties,
    };

    // The header count responds immediately. The drawer is rendered once after
    // the entire burst has settled, rather than in every cart-add response.
    this.dispatchEvent(new CartAddEvent(undefined, id.toString(), { ...eventData, phase: 'pending' }));

    const addToCartRequest = () => {
      return cartAddBatcher
        .add(cartItem)
        .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              ...eventData,
              didError: true,
              phase: 'failed',
            })
          );
          void reconcileCartState();

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent(undefined, id.toString(), {
              ...eventData,
              phase: 'confirmed',
              sections: response.cartBatchFinal ? response.sections : undefined,
            })
          );

          // The rendered cart sections can update immediately. Refresh product-page
          // quantity limits in the background without blocking the next add.
          if (response.cartBatchFinal && (this.refs.quantitySelector || this.refs.quantityLabelCartCount)) {
            void this.#fetchAndUpdateCartQuantity();
          }
        }
      });
    };

    return addToCartRequest()
      .catch((error) => {
        console.error(error);
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            ...eventData,
            didError: true,
            phase: 'failed',
          })
        );
        void reconcileCartState();
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', error?.message || 'Network error', error, {})
        );
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number, operationId: string}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const eventData = {
      source: 'product-form-component',
      optimistic: true,
      operationIds: items.map((item) => item.operationId),
      itemCount: totalQuantity,
      productId: this.dataset.productId,
    };

    const batchAddToCartRequest = () => {
      return Promise.all(
        items.map((item) =>
          cartAddBatcher.add(
            {
              id: Number.isSafeInteger(Number(item.variantId)) ? Number(item.variantId) : item.variantId,
              quantity: item.quantity,
            }
          )
        )
      )
        .then(([response]) => response)
        .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              ...eventData,
              didError: true,
              phase: 'failed',
            })
          );
          void reconcileCartState();
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        this.dispatchEvent(
          new CartAddEvent(undefined, this.id, {
            ...eventData,
            phase: 'confirmed',
            sections: response.cartBatchFinal ? response.sections : undefined,
          })
        );

        if (response.cartBatchFinal && (this.refs.quantitySelector || this.refs.quantityLabelCartCount)) {
          void this.#fetchAndUpdateCartQuantity();
        }
      });
    };

    return batchAddToCartRequest()
      .catch((error) => {
        console.error(error);
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            ...eventData,
            didError: true,
            phase: 'failed',
          })
        );
        this.dispatchEvent(
          new CartErrorEvent(this.id, error?.message || 'Network error', error, {})
        );
        void reconcileCartState();
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#variantQueueTimeout !== undefined) {
      clearTimeout(this.#variantQueueTimeout);
      this.#variantQueueTimeout = undefined;
    }

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };

  /** @param {CustomEvent<{ productId?: string, error?: unknown }>} event */
  #onVariantUpdateError = (event) => {
    if (event.detail?.productId && event.detail.productId !== this.dataset.productId) return;

    this.#variantChangeInProgress = false;
    if (this.#variantQueueTimeout !== undefined) {
      clearTimeout(this.#variantQueueTimeout);
      this.#variantQueueTimeout = undefined;
    }

    const message = event.detail?.error instanceof Error
      ? event.detail.error.message
      : 'Unable to confirm the selected product variant.';
    this.#failQueuedAdds(message);
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
